import { AGENT511, AGENT_511 } from '@rsos/constants/agent511';
import { ALI_UNDERSCORE } from '@rsos/constants/locationTypes';
import {
  EmergencyCallLocation,
  EmergencyCallSecondaryLocation,
  EmergencyConversationLocation,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import {
  UBER,
  INVISAWEAR,
  APPLE,
  GOOGLE,
  ANI_ALI,
} from '../constants/dataSources';

const formatApplicationName = (app: string): string =>
  app
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const APPLICATION_MAP: Record<string, string> = {
  [AGENT_511]: AGENT511,
  ios: APPLE,
  els: GOOGLE,
};

/**
 * Interprets the location source (application) value to be used in the 911 event's pane and popup.
 * @param {Object} location - The 911 event's location.
 * @returns String
 */
export const getDataSource = (
  location:
    | EmergencyCallSecondaryLocation
    | EmergencyCallLocation
    | EmergencyConversationLocation
    | null,
): string => {
  if (!location) return '';

  const { application, source } = location;

  if (!application) return source ? formatApplicationName(source) : '';

  if (application.startsWith(ALI_UNDERSCORE)) {
    const classOfService =
      'class_of_service' in location ? location.class_of_service : null;
    return `${ANI_ALI} (${classOfService || 'Landline'})`;
  }

  const appLower = application.toLowerCase();

  if (APPLICATION_MAP[appLower]) {
    return APPLICATION_MAP[appLower];
  }

  if (appLower.includes('uber')) return UBER;
  if (appLower.includes('invisawear')) return INVISAWEAR;

  return formatApplicationName(application);
};

export const formatValue = (value: string) => {
  return parseFloat(value).toFixed(7).replace(/0+$/, '').replace(/[.]+$/, '');
};

export const convertUnixTimeStamp = (timeStamp: number) => {
  const date = new Date(timeStamp);
  const currentTime = new Date(timeStamp).toLocaleTimeString();
  const dd = date.getDate();
  const mm = date.getMonth() + 1;
  const yy = date.getFullYear();

  return `${mm}/${dd}/${yy} | ${currentTime}`;
};

/**
 * Serializes query params, omitting null, undefined, and object values.
 * Callers such as EstimatedFloorLevel pass optional keys as undefined.
 * URLSearchParams alone would emit literal "undefined" in the query string.
 */
export const paramParser = (
  params: Record<string, string | number | boolean | null | undefined | object>,
): string => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || typeof value === 'object') {
      continue;
    }
    searchParams.set(key, String(value));
  }

  return searchParams.toString();
};
